import { colors } from '@packages/core/styles';
import { Icon, Typography } from '@packages/ui/shared';
import classNames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { ApplicableModel } from '../ssm5.constants';

import { ssm5TextStyles } from '../ssm5.styles';

/** Props for {@link ApplicableModelsTable}. */
export type ApplicableModelsTableProps = {
    /** Model year columns. */
    modelYears: readonly number[];
    /** Carline rows. */
    models: readonly ApplicableModel[];
};

const CARLINE_COLUMN_WIDTH = 196;

const useStyles = createUseStyles({
    table: {
        borderCollapse: 'separate',
        borderRadius: 4,
        borderSpacing: 0,
        overflow: 'hidden',
        tableLayout: 'fixed',
        width: '100%',
    },
    cell: {
        borderBottom: `1px solid ${colors.grayThree}`,
        borderLeft: `1px solid ${colors.grayThree}`,
        padding: '16px 16px 15px',
        textAlign: 'center',
        verticalAlign: 'middle',
        '&:first-child': {
            textAlign: 'left',
        },
        '&:last-child': {
            borderRight: `1px solid ${colors.grayThree}`,
        },
    },
    headerCell: {
        backgroundColor: colors.grayOne,
        borderTop: `1px solid ${colors.grayThree}`,
        padding: '15px 16px',
        '&:first-child': {
            width: CARLINE_COLUMN_WIDTH,
        },
    },
    bodyCell: {
        backgroundColor: colors.white,
    },
    cellContent: {
        alignItems: 'center',
        display: 'flex',
        gap: 8,
        height: 24,
        justifyContent: 'center',
        lineHeight: '24px',
    },
    carline: {
        justifyContent: 'flex-start',
        whiteSpace: 'nowrap',
    },
    note: {
        display: 'flex',
    },
    noteTooltip: {
        '& .tooltip-inner': {
            backgroundColor: colors.graySeven,
            borderRadius: 4,
            fontSize: 13,
            lineHeight: '16px',
            // the design shows the note on a single line; undo the bootstrap 200px cap
            maxWidth: 'none',
            padding: '4px 8px',
            whiteSpace: 'nowrap',
        },
    },
});

/**
 * Carline by model year matrix of the vehicles that support the SSM5 diagnostic.
 *
 * @param props - {@link ApplicableModelsTableProps}
 * @returns the table
 */
export function ApplicableModelsTable({ modelYears, models }: ApplicableModelsTableProps) {
    const classes = useStyles();
    const { t } = useTranslation();

    return (
        <table className={classes.table}>
            <thead>
                <tr>
                    <th className={classNames(classes.cell, classes.headerCell)} scope="col">
                        <div className={classNames(classes.cellContent, classes.carline)}>
                            <Typography variant="caption4">{t('ssm5:applicableModels.carlineColumn')}</Typography>
                        </div>
                    </th>
                    {modelYears.map((modelYear) => (
                        <th className={classNames(classes.cell, classes.headerCell)} key={modelYear} scope="col">
                            <div className={classes.cellContent}>
                                <Typography variant="caption4">{formatModelYear(modelYear)}</Typography>
                            </div>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {models.map((model) => (
                    <tr key={model.carline}>
                        <th className={classNames(classes.cell, classes.bodyCell)} scope="row">
                            <div className={classNames(classes.cellContent, classes.carline)}>
                                <Typography style={ssm5TextStyles.carlineLabel} variant="default">
                                    {model.carline}
                                </Typography>
                                {model.hasNote && (
                                    <OverlayTrigger
                                        overlay={
                                            <Tooltip className={classes.noteTooltip} id={`${model.carline}-note`}>
                                                {t('ssm5:applicableModels.note')}
                                            </Tooltip>
                                        }
                                        placement="bottom"
                                    >
                                        <span className={classes.note} data-testid="applicable-model-note">
                                            <Icon color="textDarkPrimary" name="info" />
                                        </span>
                                    </OverlayTrigger>
                                )}
                            </div>
                        </th>
                        {modelYears.map((modelYear) => {
                            const isSupported = model.modelYears.includes(modelYear);

                            return (
                                <td
                                    aria-label={t(
                                        isSupported
                                            ? 'ssm5:applicableModels.supported'
                                            : 'ssm5:applicableModels.notSupported'
                                    )}
                                    className={classNames(classes.cell, classes.bodyCell)}
                                    key={modelYear}
                                >
                                    <div className={classes.cellContent}>
                                        <Icon color="textDarkPrimary" name={isSupported ? 'checkmark' : 'minus'} />
                                    </div>
                                </td>
                            );
                        })}
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

/**
 * Two-digit display form of a model year, as printed in the design ("22" for 2022).
 *
 * @param modelYear - the full model year
 * @returns the last two digits of the year
 */
function formatModelYear(modelYear: number): string {
    return String(modelYear).slice(-2);
}
